import * as React from "react";
import { useParams } from "react-router-dom";
import {
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TableHead,
  Chip,
  Alert,
  Typography,
  Box,
} from "@mui/material";
import Title from "@/components/molecules/Title";
import { TitleGroup } from "@/components/molecules/TitleGroup";
import { CommonButton } from "@/components/common/buttons/CommonButton";
import { Button } from "@/components/molecules/Button";
import { ConfirmDialog } from "@/components/molecules/ConfirmDialog";
import toast from "@/components/hoc/SnackbarAlert";
import { useStatefulNavigator } from "@/util/useStatefulNavigator";
import { useLoadingPromise } from "@/hooks/useLoadingPromise";
import {
  deleteAccidentReportPersonAPI,
  getAccidentReportPersonDetailAPI,
} from "@/util/api/accidentReportPersons";
import type { AccidentReportPersonDetail } from "@/models/accidentReportPersons";
import { AccidentReportPersonsConstants as Constants } from "./AccidentReportPersonsConstants";
import type { AccidentReportPersonsSearchState } from "./AccidentReportPersons";
import {
  learningStatusColor,
  learningStatusLabel,
} from "@/components/accidentReportPersons/learningStatusDisplay";
import { formatDatetimeJA } from "@/util/datetime";

const LIST_PATH = "/accident_report_persons";

/**
 * 事故報告責任者詳細（事業者向け）。
 * 本画面は登録内容の確認とeラーニング受講状況の確認、および削除のみを提供する。
 * 兼務営業所・社内メモ・変更履歴・同一人物関係はGO社内向け画面の情報であり、APIレスポンスにも含まれない。
 */
export const AccidentReportPersonsDetail = (): JSX.Element => {
  const params = useParams<{ id: string }>();
  const id = Number(params.id);
  const { navigate, savedState } =
    useStatefulNavigator<AccidentReportPersonsSearchState>();

  const [person, setPerson] = React.useState<AccidentReportPersonDetail | null>(
    null,
  );
  const [loading, setLoading] = React.useState(true);
  const [retireDialogOpen, setRetireDialogOpen] = React.useState(false);

  const [deleting, deletePerson] = useLoadingPromise(
    deleteAccidentReportPersonAPI,
  );

  const goToList = () => navigate(LIST_PATH, savedState);

  React.useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const response = await getAccidentReportPersonDetailAPI(id);
        setPerson(response.data);
      } catch (error) {
        console.error("==Error when fetch accidentReportPerson==", error);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [id]);

  const handleRetireConfirm = async () => {
    if (deleting) return;
    try {
      await deletePerson(id);
      setRetireDialogOpen(false);
      toast.success(Constants.RetireSuccess);
      goToList();
    } catch (error) {
      console.error("==Error when delete accidentReportPerson==", error);
    }
  };

  if (!person) {
    return (
      <Grid
        container
        spacing={2}
        sx={{ paddingLeft: 2, paddingRight: 2, mt: 3 }}
      >
        <Typography>{loading ? "" : Constants.NotFoundMessage}</Typography>
      </Grid>
    );
  }

  const retireConfirmMessage = Constants.RetireConfirmMessageFormat(
    person.name ?? "",
  );

  return (
    <>
      <Grid
        container
        spacing={2}
        sx={{ paddingLeft: 2, paddingRight: 2, mt: 3 }}
      >
        <Grid container spacing={2} sx={{ display: "flex", pl: 2 }}>
          <Title style={{ flexGrow: 1 }}>{Constants.DetailTitle}</Title>
          <CommonButton
            btnType="Return"
            sx={{ height: 35, mr: 1 }}
            onClickBtn={goToList}
          />
          {/* 削除は一方通行。削除済みには操作を出さない（再有効化はGO社内向け画面のみ）。 */}
          {person.isActive && (
            <Button
              label={Constants.RetireButton}
              sx={{ height: 35, backgroundColor: "#E71A19" }}
              onClick={() => setRetireDialogOpen(true)}
            />
          )}
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ paddingLeft: 2, paddingRight: 2 }}>
        {/* 他の画面と同じく、内容は入れ子の Grid container に入れる（左端をそろえるため） */}
        <Grid container spacing={2}>
          <Grid item xs={10} md={10} lg={14}>
            {!person.isActive && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {Constants.RetiredMessage}
              </Alert>
            )}
            <Paper sx={{ p: 1, display: "flex", flexDirection: "column" }}>
              <Table size="small" className="gx-info-table">
                <TableHead>
                  <TableRow>
                    <TableCell colSpan={2} className="body-none-custom">
                      <TitleGroup
                        style={{
                          fontWeight: "bold",
                          fontSize: "1.3rem",
                          paddingTop: 20,
                          marginBottom: 0,
                        }}
                        text={Constants.BasicInformation}
                      />
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow sx={{ height: 45 }}>
                    <TableCell>{Constants.Name}</TableCell>
                    <TableCell>{person.name ?? "-"}</TableCell>
                  </TableRow>
                  <TableRow sx={{ height: 45 }}>
                    <TableCell>{Constants.Email}</TableCell>
                    <TableCell>{person.email ?? "-"}</TableCell>
                  </TableRow>
                  <TableRow sx={{ height: 45 }}>
                    <TableCell>{Constants.Phone}</TableCell>
                    <TableCell>{person.phoneNumber ?? "-"}</TableCell>
                  </TableRow>
                  <TableRow sx={{ height: 45 }}>
                    <TableCell>{Constants.PrimaryOffice}</TableCell>
                    <TableCell>{person.officeName ?? "-"}</TableCell>
                  </TableRow>
                  <TableRow sx={{ height: 45 }}>
                    <TableCell>{Constants.Status}</TableCell>
                    <TableCell>
                      <Chip
                        label={
                          person.isActive
                            ? Constants.StatusActive
                            : Constants.StatusRetired
                        }
                        color={person.isActive ? "success" : "default"}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>

              {/* 研修プログラム単位の受講状況。最新だけでなく過去分・公開終了分もすべて表示する。
                  データテーブルは見出しテーブルの中にネストせず兄弟要素にする。gx-info-table は
                  width:80% !important を無条件に付与するため、ネストすると二重に80%が掛かり
                  右端まで広がらなくなる（他の詳細画面と同じ構成）。 */}
              <Table size="small" className="gx-info-table" sx={{ mt: 2 }}>
                <TableHead>
                  <TableRow>
                    <TableCell className="body-none-custom">
                      <TitleGroup
                        style={{
                          paddingTop: 25,
                          marginBottom: 0,
                        }}
                        text={Constants.LearningStatusTitle}
                      />
                    </TableCell>
                  </TableRow>
                </TableHead>
              </Table>
              {person.learningStatuses.length === 0 ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ pt: 1 }}
                >
                  {Constants.LearningStatusEmpty}
                </Typography>
              ) : (
                <TableContainer
                  className="table-container-width"
                  sx={{ pt: 1 }}
                >
                  <Table
                    size="small"
                    className="gx-info-table"
                    sx={{ tableLayout: "fixed" }}
                  >
                    <TableHead className="list-head">
                      <TableRow>
                        <TableCell>{Constants.TableProgramHeader}</TableCell>
                        <TableCell>
                          {Constants.TableLearningStatusHeader}
                        </TableCell>
                        <TableCell>{Constants.TableDeadlineHeader}</TableCell>
                        <TableCell>{Constants.TablePassedAtHeader}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {person.learningStatuses.map((assignment) => (
                        <TableRow key={assignment.learningProgramId} hover>
                          <TableCell>
                            {assignment.learningProgramTitle ?? "-"}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={learningStatusLabel(assignment.status)}
                              color={learningStatusColor(assignment.status)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            {assignment.deadline
                              ? formatDatetimeJA(assignment.deadline)
                              : "-"}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            {assignment.passedAt
                              ? formatDatetimeJA(assignment.passedAt)
                              : "-"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Grid>

      <ConfirmDialog
        visible={retireDialogOpen}
        headerTitle={Constants.RetireDialogTitle}
        type="Delete"
        showIcon
        onOk={handleRetireConfirm}
        onCancel={() => setRetireDialogOpen(false)}
      >
        <Box sx={{ textAlign: "left" }}>
          <Typography sx={{ fontWeight: "bold" }}>
            {retireConfirmMessage.question}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ whiteSpace: "pre-wrap", mt: 1 }}
          >
            {retireConfirmMessage.note}
          </Typography>
          <Typography sx={{ fontWeight: "bold", mt: 1 }}>
            {retireConfirmMessage.contact}
          </Typography>
        </Box>
      </ConfirmDialog>
    </>
  );
};
